/**
 * LabelErrorBoundary Component
 *
 * Catches rendering errors in label components and displays
 * a fallback error label that prints correctly.
 *
 * NOTE: This must be a class component. React does not yet support
 * functional error boundaries (as of React 18).
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { LABEL_DIMENSIONS, LABEL_COLORS } from '../../utils/label-constants';
import { reportError } from '@/lib/logging';

interface LabelErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback to render instead of default error label */
  fallback?: ReactNode;
  /** Garment ID for error tracking */
  garmentId?: string;
}

interface LabelErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary for label components.
 *
 * When a label fails to render, this displays a printable error label
 * so the batch doesn't break and operators can identify which item failed.
 *
 * The error label maintains the 4x3" dimensions so print layout isn't disrupted.
 */
export class LabelErrorBoundary extends Component<
  LabelErrorBoundaryProps,
  LabelErrorBoundaryState
> {
  constructor(props: LabelErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): LabelErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Report to error tracking (Sentry, etc.)
    // reportError signature: (message: string, error?: unknown, context?: LogContext)
    reportError('Label rendering failed', error, {
      context: 'LabelErrorBoundary',
      garmentId: this.props.garmentId,
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      // Return custom fallback or default error label
      return (
        this.props.fallback ?? (
          <div
            className="label"
            data-testid="label-error"
            data-garment-id={this.props.garmentId}
            style={{
              width: LABEL_DIMENSIONS.WIDTH,
              height: LABEL_DIMENSIONS.HEIGHT,
              border: `2px solid ${LABEL_COLORS.ERROR_BORDER}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Arial, sans-serif',
              pageBreakAfter: 'always',
              backgroundColor: LABEL_COLORS.ERROR_BACKGROUND,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ textAlign: 'center', color: LABEL_COLORS.ERROR_TEXT }}>
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '18px',
                  marginBottom: '8px',
                }}
              >
                LABEL ERROR
              </div>
              <div style={{ fontSize: '12px' }}>
                {this.props.garmentId
                  ? `ID: ${this.props.garmentId}`
                  : 'Failed to render label'}
              </div>
              {this.state.error && (
                <div
                  style={{
                    fontSize: '10px',
                    marginTop: '8px',
                    color: '#991b1b',
                    maxWidth: '3in',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {this.state.error.message}
                </div>
              )}
              <div
                style={{
                  fontSize: '10px',
                  marginTop: '12px',
                  color: '#666',
                }}
              >
                Contact support with ID above
              </div>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default LabelErrorBoundary;
